import type { Cue } from "./cues";

// The game's sound, all synthesized with Web Audio (nothing to download, so
// the one-file artifact stays one file): bright sine partials for china
// clinks, filtered noise for water, sponges and the machine's spray, a bell
// for orders up, and a low murmur of the dining room under it all.
//
// Browsers only let a page make sound after a click or key press, so the
// AudioContext is made on the first one (`unlock`). Until then, and when Web
// Audio isn't there at all, everything here quietly does nothing.

const MASTER = 0.6;
const GLIDE = 0.08;

interface Loop {
  filter: BiquadFilterNode;
  gain: GainNode;
}

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private murmur: Loop | null = null;
  private wash: Loop | null = null;
  private hum: { osc: OscillatorNode; gain: GainNode } | null = null;
  /** The flat-top's sizzle and the fryer's bubbling, each as loud as how much is cooking. */
  private sizzle: Loop | null = null;
  private bubble: Loop | null = null;
  private fountain: Loop | null = null;
  private grinder: Loop | null = null;
  private _volume: number;

  /** `volume` 0 (silent) .. 1 (full). */
  constructor(volume = 1) {
    this._volume = volume;
  }

  get volume(): number {
    return this._volume;
  }

  set volume(v: number) {
    this._volume = Math.max(0, Math.min(1, v));
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(MASTER * this._volume, this.ctx.currentTime, 0.02);
  }

  /** Starts audio; call it from a click or key press. Safe to call again. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => {});
      return;
    }
    const Ctor = typeof window === "undefined" ? undefined : (window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = MASTER * this._volume;
      this.master.connect(ctx.destination);
      this.noise = noiseBuffer(ctx);
      // The dining room through the pass: a low, soft band of noise.
      this.murmur = this.loop("bandpass", 420, 0.6);
      // The machine's spray arms: a wide, watery hiss, and the pump's hum.
      this.wash = this.loop("bandpass", 1100, 0.7);
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = 58;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 220;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(lp).connect(gain).connect(this.master);
      osc.start();
      this.hum = { osc, gain };
      this.sizzle = this.loop("highpass", 3800, 0.4);
      this.bubble = this.loop("bandpass", 600, 3);
      this.fountain = this.loop("bandpass", 1800, 0.9);
      this.grinder = this.loop("bandpass", 420, 1.4);
    } catch {
      this.ctx = null;
    }
  }

  /** The background: the dining room's murmur (0 when paused) and the machine running. */
  setAmbience(room: number, washing: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.murmur || !this.wash || !this.hum) return;
    const t = ctx.currentTime;
    this.murmur.gain.gain.setTargetAtTime(room * 0.05, t, 0.4);
    // The murmur drifts, like a room of conversations.
    this.murmur.filter.frequency.setTargetAtTime(380 + Math.sin(t * 0.7) * 60 + Math.sin(t * 1.9) * 30, t, 0.3);
    this.wash.gain.gain.setTargetAtTime(washing && room > 0 ? 0.16 : 0, t, GLIDE * 2);
    this.hum.gain.gain.setTargetAtTime(washing && room > 0 ? 0.05 : 0, t, GLIDE * 2);
  }

  /** The cooking: how many patties are on the flat-top and portions in the fryer. */
  setCooking(patties: number, fries: number, running: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.sizzle || !this.bubble) return;
    const t = ctx.currentTime;
    const on = running ? 1 : 0;
    this.sizzle.gain.gain.setTargetAtTime(on * Math.min(1, patties / 2) * 0.09, t, 0.15);
    this.bubble.gain.gain.setTargetAtTime(on * Math.min(1, fries) * 0.12, t, 0.15);
    // Bubbles pop at a wandering pitch.
    this.bubble.filter.frequency.setTargetAtTime(500 + Math.sin(t * 13) * 160 + Math.sin(t * 29) * 90, t, 0.02);
  }

  /** The soda fountain running into a cup while Use is held under a nozzle. */
  setPouring(on: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.fountain) return;
    const t = ctx.currentTime;
    this.fountain.gain.gain.setTargetAtTime(on ? 0.1 : 0, t, 0.04);
    // Fizz rises in pitch as the cup fills.
    if (on) this.fountain.filter.frequency.setTargetAtTime(1800 + Math.sin(t * 17) * 250, t, 0.05);
  }

  /** The coffee grinder's burrs, while Use is held at it. */
  setGrinding(on: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.grinder) return;
    const t = ctx.currentTime;
    this.grinder.gain.gain.setTargetAtTime(on ? 0.16 : 0, t, 0.03);
    if (on) this.grinder.filter.frequency.setTargetAtTime(420 + Math.sin(t * 31) * 60, t, 0.02);
  }

  /** A footstep on quarry tile. */
  step(): void {
    this.burst({ type: "lowpass", freq: 260, q: 0.8, gain: 0.16, attack: 0.002, decay: 0.07 });
  }

  play(cue: Cue): void {
    if (!this.ctx || this._volume === 0) return;
    switch (cue) {
      case "ticket":
        // The printer chatters, then the bell.
        for (let i = 0; i < 7; i++) this.burst({ type: "highpass", freq: 3200, q: 0.7, gain: 0.09, attack: 0.001, decay: 0.018, at: i * 0.035 });
        this.bell(2093, 0.18, 0.3);
        break;
      case "pickup":
        this.clink(0.22);
        break;
      case "putBack":
        this.clink(0.28, 0.9);
        this.burst({ type: "lowpass", freq: 500, q: 0.7, gain: 0.12, attack: 0.002, decay: 0.06 });
        break;
      case "splash":
        this.burst({ type: "lowpass", freq: 1800, sweepTo: 350, q: 0.9, gain: 0.45, attack: 0.005, decay: 0.38 });
        this.burst({ type: "bandpass", freq: 900, q: 3, gain: 0.12, attack: 0.02, decay: 0.2, at: 0.06 });
        break;
      case "scrub":
        // A sponge pass: a breathy swish with a little rubber squeak.
        this.burst({ type: "bandpass", freq: 1500 + Math.random() * 500, sweepTo: 2400, q: 1.6, gain: 0.32, attack: 0.02, decay: 0.12 });
        this.tone({ freq: 950 + Math.random() * 150, to: 1400, type: "sine", gain: 0.035, attack: 0.01, decay: 0.07, at: 0.03 });
        break;
      case "clean":
        // A glint: two bright partials.
        this.tone({ freq: 1568, type: "sine", gain: 0.14, attack: 0.003, decay: 0.45, at: 0.05 });
        this.tone({ freq: 2637, type: "sine", gain: 0.1, attack: 0.003, decay: 0.55, at: 0.1 });
        break;
      case "rack":
        this.clink(0.3, 0.8, 0.12);
        break;
      case "machineStart":
        // The hood comes down: a heavy steel thunk.
        this.tone({ freq: 140, to: 55, type: "sine", gain: 0.4, attack: 0.003, decay: 0.25 });
        this.burst({ type: "lowpass", freq: 900, q: 0.8, gain: 0.25, attack: 0.002, decay: 0.12 });
        break;
      case "machineDone":
        this.bell(1319, 0.2, 0);
        this.bell(1760, 0.2, 0.18);
        break;
      case "rackOut":
        this.burst({ type: "lowpass", freq: 1200, sweepTo: 300, q: 0.7, gain: 0.18, attack: 0.05, decay: 0.5 }); // a breath of steam
        this.clink(0.2, 1, 0.05);
        break;
      case "shelve":
        for (let i = 0; i < 4; i++) this.clink(0.2, 0.9 + Math.random() * 0.25, i * 0.07);
        break;
      case "served":
        // Order up: a soft ka-ching.
        this.tone({ freq: 1976, type: "triangle", gain: 0.07, attack: 0.002, decay: 0.12 });
        this.tone({ freq: 2637, type: "triangle", gain: 0.07, attack: 0.002, decay: 0.3, at: 0.08 });
        break;
      case "walkout":
        // The swing door, and a deflated note.
        this.burst({ type: "lowpass", freq: 300, q: 0.8, gain: 0.35, attack: 0.01, decay: 0.2 });
        this.tone({ freq: 330, to: 165, type: "triangle", gain: 0.12, attack: 0.02, decay: 0.5, at: 0.1 });
        break;
      case "crash":
        this.burst({ type: "highpass", freq: 1800, q: 0.5, gain: 0.45, attack: 0.001, decay: 0.45 });
        for (let i = 0; i < 6; i++) this.clink(0.25, 0.8 + Math.random() * 0.8, Math.random() * 0.25);
        break;
      case "sizzle":
        this.burst({ type: "highpass", freq: 3000, q: 0.5, gain: 0.35, attack: 0.005, decay: 0.6 });
        this.burst({ type: "lowpass", freq: 400, q: 0.7, gain: 0.15, attack: 0.002, decay: 0.05 });
        break;
      case "flip":
        // The spatula scrapes under, then a fresh hiss.
        this.burst({ type: "bandpass", freq: 2600, sweepTo: 1600, q: 4, gain: 0.18, attack: 0.01, decay: 0.12 });
        this.burst({ type: "highpass", freq: 3200, q: 0.5, gain: 0.22, attack: 0.02, decay: 0.4, at: 0.08 });
        break;
      case "thunk":
        this.burst({ type: "lowpass", freq: 500, q: 0.8, gain: 0.22, attack: 0.002, decay: 0.07 });
        break;
      case "fryDrop":
        // A loud crackling rush as the frozen fries hit the oil.
        this.burst({ type: "highpass", freq: 2200, q: 0.4, gain: 0.45, attack: 0.01, decay: 0.9 });
        for (let i = 0; i < 6; i++) this.burst({ type: "bandpass", freq: 1500 + Math.random() * 2000, q: 6, gain: 0.08, attack: 0.001, decay: 0.03, at: Math.random() * 0.5 });
        break;
      case "fryLift":
        this.burst({ type: "bandpass", freq: 1800, q: 1.2, gain: 0.12, attack: 0.02, decay: 0.25 });
        this.clink(0.08, 0.6, 0.05);
        break;
      case "topping":
        this.burst({ type: "bandpass", freq: 900, q: 1.5, gain: 0.12, attack: 0.005, decay: 0.06 });
        break;
      case "bell":
        // Order up!
        this.bell(2349, 0.3, 0);
        break;
      case "ready":
        // A kitchen timer's two-note ding: flip it, or take it off.
        this.tone({ freq: 1319, type: "sine", gain: 0.12, attack: 0.005, decay: 0.25 });
        this.tone({ freq: 1760, type: "sine", gain: 0.12, attack: 0.005, decay: 0.35, at: 0.12 });
        break;
      case "hurry":
        // Three quick beeps: it's about to burn.
        for (let i = 0; i < 3; i++) this.tone({ freq: 1568, type: "triangle", gain: 0.08, attack: 0.004, decay: 0.07, at: i * 0.11 });
        break;
      case "burnt":
        this.tone({ freq: 880, type: "square", gain: 0.05, attack: 0.005, decay: 0.12 });
        this.tone({ freq: 880, type: "square", gain: 0.05, attack: 0.005, decay: 0.12, at: 0.2 });
        break;
      case "trash":
        this.burst({ type: "lowpass", freq: 300, q: 0.8, gain: 0.3, attack: 0.005, decay: 0.15 });
        this.burst({ type: "bandpass", freq: 1200, q: 0.7, gain: 0.08, attack: 0.01, decay: 0.2 });
        break;
      case "closing":
        this.bell(1047, 0.25, 0);
        this.bell(1319, 0.25, 0.25);
        this.bell(1568, 0.25, 0.5);
        break;
    }
  }

  dispose(): void {
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }

  /** China on china: a couple of high, inharmonic partials that ring briefly. */
  private clink(gain: number, pitch = 1, at = 0): void {
    const base = 2200 * pitch * (0.92 + Math.random() * 0.16);
    this.tone({ freq: base, type: "sine", gain, attack: 0.001, decay: 0.14, at });
    this.tone({ freq: base * 1.59, type: "sine", gain: gain * 0.6, attack: 0.001, decay: 0.09, at });
    this.tone({ freq: base * 2.43, type: "sine", gain: gain * 0.3, attack: 0.001, decay: 0.05, at });
  }

  /** A struck bell: a fundamental and its bright overtones. */
  private bell(freq: number, gain: number, at: number): void {
    this.tone({ freq, type: "sine", gain, attack: 0.002, decay: 0.9, at });
    this.tone({ freq: freq * 2.76, type: "sine", gain: gain * 0.3, attack: 0.002, decay: 0.4, at });
    this.tone({ freq: freq * 5.4, type: "sine", gain: gain * 0.12, attack: 0.002, decay: 0.2, at });
  }

  private tone(o: { freq: number; to?: number; type: OscillatorType; gain: number; attack: number; decay: number; at?: number }): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime + (o.at ?? 0);
    const osc = ctx.createOscillator();
    osc.type = o.type;
    osc.frequency.setValueAtTime(o.freq, t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t + o.attack + o.decay);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(o.gain, t + o.attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + o.attack + o.decay);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + o.attack + o.decay + 0.05);
  }

  private burst(o: { type: BiquadFilterType; freq: number; sweepTo?: number; q: number; gain: number; attack: number; decay: number; at?: number }): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.noise || this._volume === 0) return;
    const t = ctx.currentTime + (o.at ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = o.type;
    f.frequency.setValueAtTime(o.freq, t);
    if (o.sweepTo) f.frequency.exponentialRampToValueAtTime(o.sweepTo, t + o.attack + o.decay);
    f.Q.value = o.q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(o.gain, t + o.attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + o.attack + o.decay);
    src.connect(f).connect(g).connect(this.master);
    // Start somewhere random in the buffer so repeats don't sound identical.
    src.start(t, Math.random() * 1.5);
    src.stop(t + o.attack + o.decay + 0.05);
  }

  private loop(type: BiquadFilterType, freq: number, q: number): Loop {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(filter).connect(gain).connect(this.master!);
    src.start();
    return { filter, gain };
  }
}

/** Two seconds of white noise, shared by every noisy sound. */
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}
